import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://lavernaibackend.onrender.com';

export interface ProfileCompletenessData {
  isComplete: boolean;
  missingFields: string[];
  completenessPercentage: number;
  lastUpdateDate?: Date;
  daysSinceUpdate: number;
}

export interface ProfileReminderResponse {
  success: boolean;
  data?: ProfileCompletenessData;
  message?: string;
}

class ProfileReminderService {
  private baseURL: string;

  constructor() {
    this.baseURL = API_BASE_URL;
  }

  /**
   * Check current user's profile completeness
   */
  async checkProfileCompleteness(): Promise<ProfileCompletenessData> {
    try {
      console.log('Checking profile completeness...');
      
      // Wait a bit for localStorage to be available and token to be set
      let attempts = 0;
      let token = localStorage.getItem('authToken');
      
      while (!token && attempts < 10) {
        await new Promise(resolve => setTimeout(resolve, 100));
        token = localStorage.getItem('authToken');
        attempts++;
      }
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.get<ProfileReminderResponse>(`${this.baseURL}/api/profile-reminders/check`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      console.log('Profile completeness response:', response.data);
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      throw new Error('Failed to check profile completeness');
    } catch (error: any) {
      console.error('Error checking profile completeness:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
      throw new Error(`Failed to check profile completeness: ${error.message}`);
    }
  }

  /**
   * Send immediate reminder to current user
   */
  async sendImmediateReminder(reason: 'incomplete' | 'outdated' | 'visibility'): Promise<void> {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.post<ProfileReminderResponse>(`${this.baseURL}/api/profile-reminders/send-reminder`, {
        reason
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to send reminder');
      }
    } catch (error) {
      console.error('Error sending profile reminder:', error);
      throw new Error('Failed to send profile reminder');
    }
  }

  /**
   * Get profile completeness status for display
   */
  getCompletenessStatus(completeness: ProfileCompletenessData): {
    status: 'complete' | 'incomplete' | 'outdated';
    priority: 'low' | 'medium' | 'high';
    message: string;
    shouldShowReminder: boolean;
  } {
    if (completeness.isComplete && completeness.daysSinceUpdate <= 30) {
      return {
        status: 'complete',
        priority: 'low',
        message: 'Your profile is complete and up-to-date!',
        shouldShowReminder: false
      };
    }

    if (completeness.completenessPercentage < 50) {
      return {
        status: 'incomplete',
        priority: 'high',
        message: `Your profile is only ${Math.round(completeness.completenessPercentage)}% complete. Other competitors can't find you!`,
        shouldShowReminder: true
      };
    }

    if (completeness.completenessPercentage < 80) {
      return {
        status: 'incomplete',
        priority: 'medium',
        message: `Your profile is ${Math.round(completeness.completenessPercentage)}% complete. Add missing info to be found by more competitors.`,
        shouldShowReminder: true
      };
    }

    if (completeness.daysSinceUpdate > 30) {
      return {
        status: 'outdated',
        priority: 'low',
        message: `Your profile hasn't been updated in ${completeness.daysSinceUpdate} days. Keep it current to stay visible!`,
        shouldShowReminder: true
      };
    }

    return {
      status: 'complete',
      priority: 'low',
      message: 'Your profile is complete!',
      shouldShowReminder: false
    };
  }

  /**
   * Generate reminder message based on missing fields
   */
  generateReminderMessage(completeness: ProfileCompletenessData): string {
    const status = this.getCompletenessStatus(completeness);
    
    if (status.status === 'complete') {
      return 'Your profile is complete and up-to-date!';
    }

    if (completeness.missingFields.length > 0) {
      const missingFields = completeness.missingFields.slice(0, 3);
      const moreText = completeness.missingFields.length > 3 ? ' and more...' : '';
      
      return `Please complete your profile by adding: ${missingFields.join(', ')}${moreText}`;
    }

    if (completeness.daysSinceUpdate > 30) {
      return `Update your profile to stay relevant to other competitors. It's been ${completeness.daysSinceUpdate} days since your last update.`;
    }

    return 'Please complete your profile to be visible to other competitors.';
  }

  /**
   * Get priority color for UI display
   */
  getPriorityColor(priority: 'low' | 'medium' | 'high'): string {
    switch (priority) {
      case 'high':
        return '#dc2626'; // Red
      case 'medium':
        return '#f59e0b'; // Orange
      case 'low':
        return '#10b981'; // Green
      default:
        return '#6b7280'; // Gray
    }
  }

  /**
   * Get priority icon for UI display
   */
  getPriorityIcon(priority: 'low' | 'medium' | 'high'): string {
    switch (priority) {
      case 'high':
        return '🚨';
      case 'medium':
        return '⚠️';
      case 'low':
        return 'ℹ️';
      default:
        return '📝';
    }
  }
}

export default new ProfileReminderService();
